import React, { useEffect, useState, useCallback } from 'react';
import { BarChart3, TrendingUp, TrendingDown, ChevronDown, ChevronUp, RefreshCw, Coins, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from '@/i18n';
import { apiClient, QuickSummary } from '@/utils/apiClient';

interface UsageTrendsWidgetProps {
  className?: string;
  compact?: boolean;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString();
};

const formatCost = (cost: number): string => {
  if (cost === 0) return '0 ₽';
  if (cost < 0.01) return `${cost.toFixed(4)} ₽`;
  return `${cost.toFixed(2)} ₽`;
};

export const UsageTrendsWidget: React.FC<UsageTrendsWidgetProps> = ({ className, compact = false }) => {
  const { t } = useTranslation();
  const at = (t as any).analytics;
  const [data, setData] = useState<QuickSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getTokenSummary();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [at?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error ?? (at?.noData ?? 'No data')}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  const todayTokens = data.today.total_tokens;
  // Use week average as "yesterday" proxy since we have today / week / month
  const weekAvgDaily = data.week.total_tokens > 0 ? Math.round(data.week.total_tokens / 7) : 0;
  const diff = weekAvgDaily > 0 ? ((todayTokens - weekAvgDaily) / weekAvgDaily) * 100 : 0;
  const isUp = diff >= 0;

  if (compact) {
    return (
      <div className={clsx('inline-flex items-center gap-1.5 text-xs', className)}>
        <BarChart3 className="h-3 w-3 text-blue-500" />
        <span className="font-medium text-gray-700 dark:text-gray-300 tabular-nums">
          {formatNumber(todayTokens)}
        </span>
        <span className="text-gray-400 dark:text-gray-500">{at?.today?.toLowerCase() ?? 'today'}</span>
        {weekAvgDaily > 0 && (
          <span className={clsx('flex items-center gap-0.5 tabular-nums', isUp ? 'text-orange-500' : 'text-green-500')}>
            {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(diff).toFixed(0)}%
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700', className)}>
      {/* Compact header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.usageTrends ?? 'Usage Trends'}
            </h3>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium tabular-nums">{formatNumber(todayTokens)}</span>
              <span>{at?.today?.toLowerCase() ?? 'today'}</span>
              {weekAvgDaily > 0 && (
                <span className={clsx('flex items-center gap-0.5 tabular-nums', isUp ? 'text-orange-500' : 'text-green-500')}>
                  {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(diff).toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-gray-400" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400" />
        )}
      </button>

      {/* Expanded summary cards */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          <div className="grid grid-cols-2 gap-3">
            {/* Today */}
            <SummaryMiniCard
              label={at?.today ?? 'Today'}
              tokens={data.today.total_tokens}
              requests={data.today.total_requests}
              cost={data.today.total_cost_usd}
              reqLabel={at?.reqShort ?? 'req'}
              icon={Clock}
              color="blue"
            />
            {/* This Week */}
            <SummaryMiniCard
              label={at?.thisWeek ?? 'This Week'}
              tokens={data.week.total_tokens}
              requests={data.week.total_requests}
              cost={data.week.total_cost_usd}
              reqLabel={at?.reqShort ?? 'req'}
              icon={TrendingUp}
              color="green"
            />
            {/* This Month */}
            <SummaryMiniCard
              label={at?.thisMonth ?? 'This Month'}
              tokens={data.month.total_tokens}
              requests={data.month.total_requests}
              cost={data.month.total_cost_usd}
              reqLabel={at?.reqShort ?? 'req'}
              icon={BarChart3}
              color="purple"
            />
            {/* All Time */}
            <SummaryMiniCard
              label={t.stats?.allTime ?? 'All Time'}
              tokens={data.all_time.total_tokens}
              requests={data.all_time.total_requests}
              cost={data.all_time.total_cost_usd}
              reqLabel={at?.reqShort ?? 'req'}
              icon={Coins}
              color="orange"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Mini summary card used inside expanded widget
interface SummaryMiniCardProps {
  label: string;
  tokens: number;
  requests: number;
  cost: number;
  reqLabel: string;
  icon: React.FC<{ className?: string }>;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const colorMap = {
  blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400',
};

const SummaryMiniCard: React.FC<SummaryMiniCardProps> = ({ label, tokens, requests, cost, reqLabel, icon: Icon, color }) => (
  <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
    <div className="flex items-center gap-2 mb-1">
      <div className={clsx('p-1 rounded', colorMap[color])}>
        <Icon className="h-3 w-3" />
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
    </div>
    <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">
      {formatNumber(tokens)}
    </p>
    <p className="text-[10px] text-gray-400 dark:text-gray-500">
      {requests} {reqLabel} · {formatCost(cost)}
    </p>
  </div>
);
